import React, { useContext, useEffect, useState } from 'react'
import {
    Autocomplete,
    Avatar,
    Backdrop,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    Typography,
} from '@mui/material'
import {
    ArrowBack,
    Receipt,
    Pix,
    PersonAdd,
    ReceiptLongOutlined,
} from '@mui/icons-material'
import { tableCellClasses } from '@mui/material/TableCell'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { api } from '../../../services/Main/Api'
import { getCondoId } from '../../../services/Main/AuthStorage'
import Masks from '../../../components/Shared/Masks'
import Context from '../../../contexts/Context'

const CONTACT_TYPE_MAP = {
    CLIENT: { label: 'Cliente', color: '#3B82F6' },
    EMPLOYEE: { label: 'Funcionário', color: '#8B5CF6' },
    SUPPLIER: { label: 'Fornecedor', color: '#F59E0B' },
    BUSINESS_PARTNER: { label: 'Sócio', color: '#22C55E' },
}

const PaymentsCreate = () => {
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const { unitOptions, getUnitOptions } = useContext(Context)

    const [saving, setSaving] = useState(false)
    const [reqData, setReqData] = useState({
        due_date: null,
        payment_date: null,
        description: '',
        reference: '',
        value: '',
        notes: '',
        payment_method: null,
        barcode: '',
        pix_key: '',
    })

    // Options
    const [accounts, setAccounts] = useState([])
    const [categories, setCategories] = useState([])

    // Selected values
    const [selectedAccount, setSelectedAccount] = useState(null)
    const [selectedUnit, setSelectedUnit] = useState(null)
    const [selectedCategory, setSelectedCategory] = useState(null)

    // Inline contact creation
    const [createContactOpen, setCreateContactOpen] = useState(false)
    const [contactSaving, setContactSaving] = useState(false)
    const [contactErrors, setContactErrors] = useState({})
    const [newContact, setNewContact] = useState({ name: '', cpf: '', email: '', phone: '', contact_type: 'CLIENT' })

    const isFitBankAccount = selectedAccount?.is_fitbank === true

    useEffect(() => {
        if (!unitOptions) getUnitOptions()
    }, [])

    useEffect(() => {
        const condoId = getCondoId()
        api.get('cashflow/managed-bank-accounts/', { params: { condo_id: condoId, is_active: true } })
            .then((res) => {
                const list = res.data?.results || res.data || []
                setAccounts(list)
                const fitbank = list.find((acc) => acc.is_fitbank)
                if (fitbank) setSelectedAccount(fitbank)
            })
            .catch(() => {})

        api.get('cashflow/categories/', { params: { condo_id: condoId, active: true } })
            .then((res) => setCategories(res.data?.results || res.data || []))
            .catch(() => {})
    }, [])

    // Reset payment method when account changes
    useEffect(() => {
        if (!isFitBankAccount) {
            setReqData((prev) => ({ ...prev, payment_method: null, barcode: '', pix_key: '' }))
        }
    }, [isFitBankAccount])

    const parsedValue = +(reqData.value?.toString().replace(/\D+/g, '') || 0)

    function validate() {
        if (parsedValue <= 0) {
            enqueueSnackbar('Informe um valor.', { variant: 'warning' })
            return false
        }
        if (!reqData.due_date) {
            enqueueSnackbar('Informe a data de vencimento.', { variant: 'warning' })
            return false
        }
        if (isFitBankAccount && reqData.payment_method === 'boleto' && !reqData.barcode.trim()) {
            enqueueSnackbar('Informe o código de barras do boleto.', { variant: 'warning' })
            return false
        }
        if (isFitBankAccount && reqData.payment_method === 'pix' && !reqData.pix_key.trim()) {
            enqueueSnackbar('Informe a chave PIX copia e cola.', { variant: 'warning' })
            return false
        }
        return true
    }

    // --- Contact creation ---
    const handleOpenCreateContact = () => {
        setNewContact({ name: '', cpf: '', email: '', phone: '', contact_type: 'CLIENT' })
        setContactErrors({})
        setCreateContactOpen(true)
    }

    const handleSaveContact = async () => {
        const errors = {}
        if (!newContact.name?.trim()) errors.name = 'Nome é obrigatório'
        if (!newContact.cpf?.replace(/\D/g, '')?.trim()) errors.cpf = 'CPF/CNPJ é obrigatório'

        if (Object.keys(errors).length > 0) {
            setContactErrors(errors)
            return
        }

        setContactSaving(true)
        setContactErrors({})

        try {
            const cleanCpf = newContact.cpf.replace(/\D/g, '')
            const res = await api.post('/unit/', {
                condo: +getCondoId(),
                name: newContact.name.trim(),
                cpf: cleanCpf,
                email: newContact.email?.trim() || '',
                phone: newContact.phone?.replace(/\D/g, '') || '',
                number: cleanCpf,
                contact_type: newContact.contact_type,
                owner: true,
            })

            const created = res.data
            enqueueSnackbar('Contato criado com sucesso!', { variant: 'success' })
            getUnitOptions()
            setSelectedUnit({
                label: `${created.number} | ${created.name}`,
                value: created.id,
                key: created.id,
            })
            setCreateContactOpen(false)
        } catch (error) {
            const errData = error?.response?.data
            if (errData && typeof errData === 'object') {
                const fieldErrors = {}
                Object.entries(errData).forEach(([key, val]) => {
                    fieldErrors[key] = Array.isArray(val) ? val.join(', ') : val
                })
                setContactErrors(fieldErrors)
            }
            enqueueSnackbar('Erro ao criar contato.', { variant: 'error' })
        } finally {
            setContactSaving(false)
        }
    }

    async function handleSave() {
        if (!validate()) return

        setSaving(true)

        const data = {
            condo: +getCondoId(),
            account: selectedAccount?.id || null,
            due_date: reqData.due_date ? moment(reqData.due_date).format('YYYY-MM-DD') : null,
            payment_date: reqData.payment_date ? moment(reqData.payment_date).format('YYYY-MM-DD') : null,
            contact: selectedUnit?.value || null,
            description: reqData.description,
            reference: reqData.reference,
            category: selectedCategory?.id || null,
            value: (parsedValue / 100).toFixed(2),
            status: 'pending',
            notes: reqData.notes,
        }

        // FitBank payment data
        if (isFitBankAccount && reqData.payment_method) {
            data.payment_method = reqData.payment_method
            if (reqData.payment_method === 'boleto') {
                data.barcode = reqData.barcode
            } else if (reqData.payment_method === 'pix') {
                data.pix_key = reqData.pix_key
            }
        }

        try {
            await api.post('cashflow/payments/', data)
            enqueueSnackbar('Pagamento criado com sucesso!', { variant: 'success' })
            navigate('/financeiro/pagamentos')
        } catch (error) {
            enqueueSnackbar('Erro ao criar pagamento.', { variant: 'error' })
            console.log(error)
        } finally {
            setSaving(false)
        }
    }

    const methodCardStyle = (method) => ({
        flex: 1,
        border: reqData.payment_method === method ? '2px solid #3B82F6' : '1px solid #E2E8F0',
        borderRadius: 2,
        p: 2,
        cursor: 'pointer',
        textAlign: 'center',
        bgcolor: reqData.payment_method === method ? '#EFF6FF' : '#fff',
        transition: 'all 0.15s ease',
        '&:hover': {
            borderColor: '#3B82F6',
            bgcolor: '#F8FAFC',
        },
    })

    return (
        <>
            <Helmet>
                <title>Novo Pagamento - Bloco B3</title>
            </Helmet>
            <Backdrop sx={{ color: '#fff', zIndex: 1500 }} open={saving}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>
                <Container maxWidth="lg" sx={{ py: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <IconButton
                            size="small"
                            onClick={() => navigate('/financeiro/pagamentos')}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', ml: 1 }}>
                            Novo pagamento
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {/* Left side - Form */}
                        <Grid item xs={12} lg={8}>
                            <Card
                                sx={{
                                    border: '1px solid #E2E8F0',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                }}
                            >
                                <CardContent sx={{ px: 3, pb: 3, pt: 2 }}>
                                    {/* Conta */}
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 0.5, display: 'block' }}>
                                            Conta
                                        </Typography>
                                        <Autocomplete
                                            options={accounts}
                                            getOptionLabel={(opt) => opt.nickname || ''}
                                            value={selectedAccount}
                                            onChange={(_, val) => setSelectedAccount(val)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Selecione uma conta"
                                                    size="small"
                                                />
                                            )}
                                            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                                        />
                                    </Box>

                                    {/* Vencimento + Pagamento */}
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 0.5, display: 'block' }}>
                                                Vencimento
                                            </Typography>
                                            <DesktopDatePicker
                                                value={reqData.due_date}
                                                onChange={(val) =>
                                                    setReqData((prev) => ({ ...prev, due_date: val }))
                                                }
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        inputProps: { readOnly: true },
                                                    },
                                                }}
                                                sx={{ width: '100%' }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 0.5, display: 'block' }}>
                                                Pagamento <Typography component="span" variant="caption" sx={{ color: '#94A3B8' }}>(opcional)</Typography>
                                            </Typography>
                                            <DesktopDatePicker
                                                value={reqData.payment_date}
                                                onChange={(val) =>
                                                    setReqData((prev) => ({ ...prev, payment_date: val }))
                                                }
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        inputProps: { readOnly: true },
                                                    },
                                                }}
                                                sx={{ width: '100%' }}
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Contato */}
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 0.5, display: 'block' }}>
                                            Contato
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                            <Autocomplete
                                                sx={{ flex: 1 }}
                                                options={unitOptions || []}
                                                getOptionLabel={(opt) => opt.label || ''}
                                                value={selectedUnit}
                                                onChange={(_, val) => setSelectedUnit(val)}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Selecione um contato..."
                                                        size="small"
                                                    />
                                                )}
                                                renderOption={(props, option) => {
                                                    const typeInfo = CONTACT_TYPE_MAP[option.contact_type]
                                                    return (
                                                        <li {...props} key={option.key}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                                <Typography variant="body2">{option.label}</Typography>
                                                                {typeInfo && (
                                                                    <Chip
                                                                        label={typeInfo.label}
                                                                        size="small"
                                                                        sx={{
                                                                            ml: 1,
                                                                            height: 20,
                                                                            fontSize: '0.65rem',
                                                                            fontWeight: 600,
                                                                            bgcolor: `${typeInfo.color}14`,
                                                                            color: typeInfo.color,
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </li>
                                                    )
                                                }}
                                                isOptionEqualToValue={(opt, val) => opt.value === val?.value}
                                            />
                                            <Button
                                                variant="outlined"
                                                onClick={handleOpenCreateContact}
                                                startIcon={<PersonAdd />}
                                                sx={{
                                                    minWidth: 'auto',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    borderColor: '#E2E8F0',
                                                    color: '#3B82F6',
                                                    whiteSpace: 'nowrap',
                                                    height: 40,
                                                    '&:hover': { borderColor: '#3B82F6', bgcolor: '#EFF6FF' },
                                                }}
                                            >
                                                Novo
                                            </Button>
                                        </Box>
                                    </Box>
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 0.5, display: 'block' }}>
                                                {'Descrição'} <Typography component="span" variant="caption" sx={{ color: '#94A3B8' }}>(opcional)</Typography>
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder={'Ex.: Serviço prestado'}
                                                value={reqData.description}
                                                onChange={(e) => setReqData((prev) => ({ ...prev, description: e.target.value }))}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 0.5, display: 'block' }}>
                                                {'Referência'} <Typography component="span" variant="caption" sx={{ color: '#94A3B8' }}>(opcional)</Typography>
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={reqData.reference}
                                                onChange={(e) => setReqData((prev) => ({ ...prev, reference: e.target.value }))}
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Categoria, Valor */}
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 0.5, display: 'block' }}>
                                                Categoria
                                            </Typography>
                                            <Autocomplete
                                                options={categories.sort((a, b) => {
                                                    const groupA = a.group_detail?.name || 'Sem grupo'
                                                    const groupB = b.group_detail?.name || 'Sem grupo'
                                                    return groupA.localeCompare(groupB)
                                                })}
                                                getOptionLabel={(opt) => opt.name || ''}
                                                groupBy={(opt) => opt.group_detail?.name || 'Sem grupo'}
                                                value={selectedCategory}
                                                onChange={(_, val) => setSelectedCategory(val)}
                                                renderGroup={(params) => (
                                                    <li key={params.key}>
                                                        <Typography
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                fontWeight: 700,
                                                                color: '#94A3B8',
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.05em',
                                                                px: 2,
                                                                pt: 1.5,
                                                                pb: 0.5,
                                                            }}
                                                        >
                                                            {params.group}
                                                        </Typography>
                                                        {params.children}
                                                    </li>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Selecione uma categoria..."
                                                        size="small"
                                                    />
                                                )}
                                                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 0.5, display: 'block' }}>
                                                Valor
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="(0,00)"
                                                value={Masks.money(String(reqData.value ?? ''))}
                                                onChange={(e) => setReqData((prev) => ({ ...prev, value: e.target.value }))}
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* FitBank Payment Method */}
                                    {isFitBankAccount && (
                                        <>
                                            <Divider sx={{ my: 2 }} />
                                            <Typography variant="subtitle2" sx={{ color: '#1E293B', fontWeight: 700, mb: 1.5 }}>
                                                {'Método de pagamento'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                                <Box
                                                    sx={methodCardStyle('boleto')}
                                                    onClick={() => setReqData((prev) => ({ ...prev, payment_method: 'boleto', pix_key: '' }))}
                                                >
                                                    <Receipt sx={{ fontSize: 32, color: reqData.payment_method === 'boleto' ? '#3B82F6' : '#94A3B8', mb: 0.5 }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: reqData.payment_method === 'boleto' ? '#3B82F6' : '#1E293B' }}>
                                                        Boleto
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                                        {'Código de barras'}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={methodCardStyle('pix')}
                                                    onClick={() => setReqData((prev) => ({ ...prev, payment_method: 'pix', barcode: '' }))}
                                                >
                                                    <Pix sx={{ fontSize: 32, color: reqData.payment_method === 'pix' ? '#3B82F6' : '#94A3B8', mb: 0.5 }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: reqData.payment_method === 'pix' ? '#3B82F6' : '#1E293B' }}>
                                                        PIX
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                                        Copia e cola
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {reqData.payment_method === 'boleto' && (
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 0.5, display: 'block' }}>
                                                        {'Código de barras'}
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        placeholder="Digite ou cole o código de barras do boleto"
                                                        value={reqData.barcode}
                                                        onChange={(e) => setReqData((prev) => ({ ...prev, barcode: e.target.value }))}
                                                    />
                                                </Box>
                                            )}

                                            {reqData.payment_method === 'pix' && (
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 0.5, display: 'block' }}>
                                                        Chave PIX copia e cola
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        multiline
                                                        minRows={2}
                                                        placeholder="Cole a chave PIX copia e cola"
                                                        value={reqData.pix_key}
                                                        onChange={(e) => setReqData((prev) => ({ ...prev, pix_key: e.target.value }))}
                                                    />
                                                </Box>
                                            )}
                                        </>
                                    )}

                                    <Divider sx={{ my: 2 }} />

                                    {/* Action Buttons */}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => navigate('/financeiro/pagamentos')}
                                            sx={{
                                                textTransform: 'none',
                                                color: '#64748B',
                                                borderColor: '#E2E8F0',
                                                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
                                            }}
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={handleSave}
                                            disabled={saving}
                                            sx={{
                                                bgcolor: '#10B981',
                                                '&:hover': { bgcolor: '#059669' },
                                                textTransform: 'none',
                                                fontWeight: 600,
                                            }}
                                        >
                                            Salvar pagamento
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Right side - Summary */}
                        <Grid item xs={12} lg={4}>
                            <Card
                                sx={{
                                    border: '1px solid #E2E8F0',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                    position: 'sticky',
                                    top: 24,
                                }}
                            >
                                <CardHeader
                                    avatar={<Avatar sx={{ bgcolor: 'primary.main' }}><ReceiptLongOutlined /></Avatar>}
                                    title="Resumo do Pagamento"
                                />
                                <Divider sx={{ mb: '.3rem' }} />
                                <CardContent>
                                    <TableContainer>
                                        <Table size="small" sx={{
                                            [`& .${tableCellClasses.root}`]: {
                                                borderBottom: 'none',
                                                py: 0.75,
                                            }
                                        }}>
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ color: '#64748B', fontWeight: 500, pl: 0 }}>Conta</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#1E293B', pr: 0 }}>
                                                        {selectedAccount?.nickname || '—'}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ color: '#64748B', fontWeight: 500, pl: 0 }}>Contato</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#1E293B', pr: 0 }}>
                                                        {selectedUnit?.label?.split(' | ')?.[1] || selectedUnit?.label || '—'}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ color: '#64748B', fontWeight: 500, pl: 0 }}>Vencimento</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#1E293B', pr: 0 }}>
                                                        {reqData.due_date ? moment(reqData.due_date).format('DD/MM/YYYY') : '—'}
                                                    </TableCell>
                                                </TableRow>
                                                {reqData.payment_date && (
                                                    <TableRow>
                                                        <TableCell sx={{ color: '#64748B', fontWeight: 500, pl: 0 }}>Pagamento</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 600, color: '#1E293B', pr: 0 }}>
                                                            {moment(reqData.payment_date).format('DD/MM/YYYY')}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {selectedCategory && (
                                                    <TableRow>
                                                        <TableCell sx={{ color: '#64748B', fontWeight: 500, pl: 0 }}>Categoria</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 600, color: '#1E293B', pr: 0 }}>
                                                            {selectedCategory.name}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {reqData.description && (
                                                    <TableRow>
                                                        <TableCell sx={{ color: '#64748B', fontWeight: 500, pl: 0 }}>{'Descrição'}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 600, color: '#1E293B', pr: 0 }}>
                                                            {reqData.description}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {reqData.payment_method && (
                                                    <TableRow>
                                                        <TableCell sx={{ color: '#64748B', fontWeight: 500, pl: 0 }}>{'Método'}</TableCell>
                                                        <TableCell align="right" sx={{ pr: 0 }}>
                                                            <Chip
                                                                icon={reqData.payment_method === 'boleto' ? <Receipt sx={{ fontSize: 14 }} /> : <Pix sx={{ fontSize: 14 }} />}
                                                                label={reqData.payment_method === 'boleto' ? 'Boleto' : 'PIX'}
                                                                size="small"
                                                                sx={{ fontWeight: 600, bgcolor: '#EFF6FF', color: '#3B82F6' }}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Divider sx={{ my: 1.5 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.5 }}>
                                        <Typography variant="subtitle2" sx={{ color: '#64748B', fontWeight: 600 }}>
                                            Valor total
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                            {Masks.money(String(parsedValue))}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Create Contact Dialog */}
            <Dialog
                open={createContactOpen}
                onClose={() => setCreateContactOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, color: '#1E293B', pb: 1 }}>
                    Novo Contato
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
                        {'Preencha os dados do contato. Após salvar, ele será selecionado automaticamente.'}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Nome *"
                                value={newContact.name}
                                onChange={(e) => setNewContact((prev) => ({ ...prev, name: e.target.value }))}
                                error={!!contactErrors.name}
                                helperText={contactErrors.name}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="CPF/CNPJ *"
                                value={Masks.cpfOrCnpj(newContact.cpf)}
                                onChange={(e) => setNewContact((prev) => ({ ...prev, cpf: e.target.value }))}
                                error={!!contactErrors.cpf}
                                helperText={contactErrors.cpf}
                                inputProps={{ maxLength: 18 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                select
                                label="Tipo de contato"
                                value={newContact.contact_type}
                                onChange={(e) => setNewContact((prev) => ({ ...prev, contact_type: e.target.value }))}
                            >
                                {Object.entries(CONTACT_TYPE_MAP).map(([key, { label, color }]) => (
                                    <MenuItem key={key} value={key}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                                            {label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="E-mail"
                                type="email"
                                value={newContact.email}
                                onChange={(e) => setNewContact((prev) => ({ ...prev, email: e.target.value }))}
                                error={!!contactErrors.email}
                                helperText={contactErrors.email}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Telefone"
                                value={Masks.phone(newContact.phone || '')}
                                onChange={(e) => setNewContact((prev) => ({ ...prev, phone: e.target.value }))}
                                error={!!contactErrors.phone}
                                helperText={contactErrors.phone}
                                inputProps={{ maxLength: 15 }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setCreateContactOpen(false)}
                        sx={{ textTransform: 'none', color: '#64748B' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveContact}
                        disabled={contactSaving}
                        sx={{
                            bgcolor: '#3B82F6',
                            '&:hover': { bgcolor: '#2563EB' },
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        {contactSaving ? <CircularProgress size={20} color="inherit" /> : 'Salvar Contato'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default PaymentsCreate
